import { createContext, useContext, useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, getConfig, saveAs, useSession } from '@agribot/polygon-checks-ui'
import type { ReviewStore } from './store'
import type { ExportFormat, ExportJob, ExportLevel, FlagTarget } from './types'
import type { Shock } from './scenario'

export const ReviewStoreContext = createContext<ReviewStore | null>(null)

export function useReviewStore(): ReviewStore {
  const store = useContext(ReviewStoreContext)
  if (!store) throw new Error('useReviewStore outside the sourcing section')
  return store
}

const keys = {
  all: ['sourcing'] as const,
  queue: ['sourcing', 'queue'] as const,
  submission: (id: string) => ['sourcing', 'submission', id] as const,
}

/** The signed-in reviewer's name, for a store that needs telling; the API takes it from the session. */
function useReviewer() {
  const { data } = useSession()
  return data?.full_name ?? 'Reviewer'
}

export const useQueue = () => {
  const store = useReviewStore()
  return useQuery({ queryKey: keys.queue, queryFn: () => store.queue() })
}

export const useDashboard = () => {
  const store = useReviewStore()
  return useQuery({ queryKey: ['sourcing', 'dashboard'], queryFn: () => store.dashboard() })
}

export const useOriginSupply = (originId: string | null) => {
  const store = useReviewStore()
  return useQuery({ queryKey: ['sourcing', 'supply', originId], queryFn: () => store.originSupply(originId!), enabled: Boolean(originId) })
}

export const useSupplyPlots = (originId: string, filter: { organisation?: string; farmer?: string }, enabled: boolean) => {
  const store = useReviewStore()
  return useQuery({ queryKey: ['sourcing', 'supply-plots', originId, filter], queryFn: () => store.supplyPlots(originId, filter), enabled })
}

export const useSupplyGeojson = (originId: string | null) => {
  const store = useReviewStore()
  return useQuery({ queryKey: ['sourcing', 'supply-geojson', originId], queryFn: () => store.supplyGeojson(originId!), enabled: Boolean(originId) })
}

export const useSubmissionDocuments = (id: string) => {
  const store = useReviewStore()
  return useQuery({ queryKey: ['sourcing', 'documents', id], queryFn: () => store.documents(id) })
}

/** A scenario worked out by the server; the last result stays on screen while the next is worked out. */
export const useScenario = (campaignId: string | null, shocks: Shock[], includeDrafts = false) => {
  const store = useReviewStore()
  return useQuery({
    queryKey: ['sourcing', 'scenario', campaignId, shocks, includeDrafts],
    queryFn: () => store.scenario(campaignId!, shocks, includeDrafts),
    enabled: Boolean(campaignId),
    placeholderData: keepPreviousData,
    retry: false,
  })
}

export const useStructure = (parentCompanyId: string) => {
  const store = useReviewStore()
  return useQuery({ queryKey: ['sourcing', 'structure', parentCompanyId], queryFn: () => store.structure(parentCompanyId) })
}

export const useSubmission = (id: string) => {
  const store = useReviewStore()
  const by = useReviewer()
  return useQuery({ queryKey: keys.submission(id), queryFn: () => store.open(id, by) })
}

function useChange<A>(run: (store: ReviewStore, args: A, by: string) => Promise<unknown>) {
  const store = useReviewStore()
  const by = useReviewer()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (args: A) => run(store, args, by),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.all }),
  })
}

export const useAddFlag = (id: string) =>
  useChange((s, a: { target: FlagTarget; note: string }, by) => s.addFlag(id, a.target, a.note, by))
export const useRemoveFlag = (id: string) => useChange((s, flagId: string, by) => s.removeFlag(id, flagId, by))
export const useApprove = (id: string) => useChange((s, comment: string | undefined, by) => s.approve(id, comment, by))
export const useReturn = (id: string) => useChange((s, comment: string, by) => s.returnToSupplier(id, comment, by))

/** Opens a finished export: through the API client when the API serves the file itself, else by following the signed link. */
async function openExport(job: ExportJob) {
  const url = job.download?.url
  if (!url) throw new Error(job.error ?? 'The export has no file.')
  const base = getConfig().baseUrl
  if (url.startsWith(base)) {
    saveAs(await api<Blob>(url.slice(base.length)), job.filename ?? 'export')
    return
  }
  const a = document.createElement('a')
  a.href = url
  document.body.appendChild(a)
  a.click()
  a.remove()
}

/**
 * An export built in the background: starts the job, polls it until the
 * file is ready, then downloads it. Big campaigns take a while; the screen
 * shows the job's state meanwhile.
 */
export function useExport() {
  const store = useReviewStore()
  const [job, setJob] = useState<ExportJob | null>(null)
  const run = useMutation({
    mutationFn: async ({ format, level }: { format: ExportFormat; level?: ExportLevel }) => {
      let current = await store.startExport(format, level)
      setJob(current)
      for (let wait = 500; current.status === 'queued' || current.status === 'running'; wait = Math.min(wait * 1.5, 5000)) {
        await new Promise((r) => setTimeout(r, wait))
        current = await store.exportJob(current.id)
        setJob(current)
      }
      if (current.status === 'failed') throw new Error(current.error ?? 'The export failed.')
      await openExport(current)
      return current
    },
  })
  return { run, job }
}
